import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth/auth.service';
import { ImpersonationService } from '../../shared/services/impersonation.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';


@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private route: Router, 
        private authService: AuthService,
        private impersonationService: ImpersonationService
    ) { }
    
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((err) => {
                if ([401, 403].indexOf(err.status) !== -1) {
                    console.log('🚨 Error 401/403 detectado - Token vencido o no autorizado');
                    
                    // Verificar si está impersonalizado
                    const isImpersonating = this.impersonationService.isImpersonating();
                    
                    if (isImpersonating) {
                        console.log('🔄 Está impersonalizado - Iniciando limpieza de sesión corrupta');
                        
                        // Intentar restaurar usuario original
                        try {
                            const originalUser = this.impersonationService.getOriginalUser();
                            
                            if (originalUser && this.isTokenValid(originalUser.JwtToken)) {
                                console.log('✅ Restaurando usuario original con token válido');
                                this.impersonationService.forceRestoreOriginalUser();
                                
                                // Verificar si el usuario original aún es válido
                                if (this.authService.getUser && this.authService.getUser.JwtToken) {
                                    console.log('✅ Usuario original restaurado exitosamente');
                                    return throwError(err); // Permitir que la petición se reintente
                                }
                            }
                            
                            console.log('❌ Usuario original no válido o token vencido');
                        } catch (error) {
                            console.error('❌ Error al restaurar usuario original:', error);
                        }
                        
                        // Si falla la restauración, limpiar todo
                        this.performFullCleanup('Impersonalización corrupta');
                    } else {
                        // Session normal vencida
                        console.log('🔄 Sesión normal vencida - Limpiando datos de usuario');
                        this.performFullCleanup('Token vencido');
                    }
                }
                return throwError(err);
            })
        );
    }
    
    /**
     * Realiza una limpieza completa y redirige al login
     */
    private performFullCleanup(reason: string): void {
        console.log(`🧹 Realizando limpieza completa por: ${reason}`);
        
        try {
            // Limpiar datos de autenticación
            this.authService.currentUser.next(null);
            this.authService.CleanUserLocalStorage();
            this.authService.CleanRememberLocalStorage();
            
            // Limpiar datos de impersonalización
            if (this.impersonationService.isImpersonating()) {
                this.impersonationService.forceRestoreOriginalUser();
            }
            
            // Limpiar datos adicionales del AuthService
            this.authService.cleanImpersonationDataFromAuth();
            
            console.log('✅ Limpieza completa finalizada');
        } catch (error) {
            console.error('❌ Error durante la limpieza:', error);
            
            // Limpieza de emergencia - limpiar localStorage completo relacionado con auth
            const authKeys = ['currentUser', 'remember', 'originalUser', 'impersonatedUserData'];
            authKeys.forEach(key => {
                if (localStorage.getItem(key)) {
                    localStorage.removeItem(key);
                }
            });
        }
        
        // Redirigir al login
        this.route.navigateByUrl('login-form');
    }
    
    /**
     * Verifica si un token JWT es válido (no vencido)
     */
    private isTokenValid(token: string): boolean {
        if (!token) return false;
        
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const exp = payload.exp * 1000; // Convertir a milisegundos
            const now = Date.now();
            
            return exp > now;
        } catch (error) {
            console.error('Error al validar token:', error);
            return false;
        }
    }
}
